'use client'

import { useEffect, useRef, useState } from 'react'
import { ShieldOff } from 'lucide-react'
import { AlertDialog, AlertDialogContent, AlertDialogTitle } from '@elmnts/ui/alert-dialog'
import { Button } from '@elmnts/ui/button'
import { TEXTO_REDE_BLOQUEADA, liberar } from '@/lib/auth/redeBloqueada'
import { renovar } from '@/lib/auth/renovacao'

export const TEXTO_SEM_CONEXAO_NO_BLOQUEIO = 'Sem conexão com o servidor agora. Tente de novo em instantes.'

export interface TelaDeRedeBloqueadaProps {
  /** `sessao-morta`/`saida-pendente` na tentativa. App aberto: `sairPorSessaoMorta`; abertura: `/login` sem aviso. */
  aoPerderSessao: () => void
  /** "Sair": o logout do `AuthContext` (app aberto) ou a saída da abertura (`PortaoDeSessao`). */
  aoSair: () => void | Promise<void>
}

/**
 * A tela de bloqueio por rede (faixa 28 §4). Ver `TelaDeRedeBloqueada.md`. Cobre a tela inteira; nada aqui apaga
 * estado. "Tentar de novo" = UMA renovação, com os dois botões desligados enquanto voa.
 *
 * AlertDialog do Radix em portal (D7): vira a camada de CIMA da pilha do Radix — recebe clique e foco e prende o foco
 * mesmo com um Dialog/Sheet modal aberto por baixo, que não fecha. Aberta enquanto montada; quem hospeda a desmonta.
 */
export function TelaDeRedeBloqueada({ aoPerderSessao, aoSair }: TelaDeRedeBloqueadaProps) {
  const [ocupada, setOcupada] = useState(false)
  const [semConexao, setSemConexao] = useState(false)
  const tentarRef = useRef<HTMLButtonElement>(null)
  // Quem tinha o foco quando a tela abriu (ex.: um campo do Dialog de baixo). Lido no RENDER inicial: os effects do
  // FocusScope (filho) rodam antes dos deste componente e já teriam movido o foco pro "Tentar de novo".
  const [focoAnterior] = useState<Element | null>(() =>
    typeof document === 'undefined' ? null : document.activeElement,
  )

  // Foco no "Tentar de novo" ao abrir e de volta nele quando uma tentativa termina (o `disabled` o tirou).
  useEffect(() => {
    if (!ocupada) tentarRef.current?.focus()
  }, [ocupada])

  async function tentar(): Promise<void> {
    if (ocupada) return
    setOcupada(true)
    setSemConexao(false)
    const resultado = await renovar()
    if (resultado.tipo === 'renovada') {
      // Quem hospeda some com a tela pela assinatura do store (e, no app aberto, refaz as consultas que falharam).
      liberar()
      return
    }
    if (resultado.tipo === 'sessao-morta' || resultado.tipo === 'saida-pendente') {
      aoPerderSessao()
      return
    }
    setSemConexao(resultado.tipo === 'indisponivel')
    setOcupada(false)
  }

  async function sair(): Promise<void> {
    if (ocupada) return
    setOcupada(true)
    await aoSair()
  }

  return (
    <AlertDialog open>
      <AlertDialogContent
        aria-describedby={undefined}
        // Sem `onOpenAutoFocus` próprio o Radix foca o `AlertDialogCancel`, que esta tela não tem.
        onOpenAutoFocus={(evento) => {
          evento.preventDefault()
          tentarRef.current?.focus()
        }}
        onEscapeKeyDown={(evento) => evento.preventDefault()}
        // O Dialog modal do Radix (1.1.18, `dist/index.mjs:148-151`) devolve o foco ao TRIGGER no fechamento; esta tela
        // não tem trigger e o foco caía no body. Devolve a quem o tinha ao abrir, se ainda está no documento.
        onCloseAutoFocus={(evento) => {
          evento.preventDefault()
          if (focoAnterior instanceof HTMLElement && focoAnterior.isConnected) focoAnterior.focus()
        }}
        className="inset-0 top-0 left-0 z-[1000] flex h-dvh max-w-none translate-x-0 translate-y-0 items-center justify-center rounded-none border-0 p-6 shadow-none data-[size=default]:sm:max-w-none"
      >
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <ShieldOff aria-hidden className="size-10 text-muted-foreground" />
          <AlertDialogTitle className="text-base font-medium">{TEXTO_REDE_BLOQUEADA}</AlertDialogTitle>
          {semConexao && (
            <p role="status" className="text-sm text-muted-foreground">
              {TEXTO_SEM_CONEXAO_NO_BLOQUEIO}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-2">
            <Button ref={tentarRef} type="button" onClick={() => void tentar()} disabled={ocupada} aria-busy={ocupada}>
              Tentar de novo
            </Button>
            <Button type="button" variant="outline" onClick={() => void sair()} disabled={ocupada}>
              Sair
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
